import type { ChangeEvent } from "react";
import { useMemo, useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import noop from "lodash/noop";
import { useTranslation } from "react-i18next";
import { usePrevious } from "react-use";

import { MetricRangeType } from "@waterly/graphql-types";

import { SeverityColor } from "../../SeveritySelect/severity.utils";
import type { MetricRangeCardMetricRange } from "../metricRangeUtils";
import { getInequalitySymbol, InequalityOperator, buildRangeExpression } from "../metricRangeUtils";

import type { MetricRangeTagOption } from "./MetricRangeTagSelect";
import type { RangeColorNameEmailDeleteControlProps } from "./RangeColorNameEmailDeleteControl";
import RangeColorNameEmailDeleteControl from "./RangeColorNameEmailDeleteControl";

const lessEqualSymbol = getInequalitySymbol(InequalityOperator.LESS_THAN, true);

export type IdealRangeConfigProps = {
  metricRangeType: MetricRangeType;
  idealRange: MetricRangeCardMetricRange;
  metricRangeTagOptions: MetricRangeTagOption[];
  onLowerLimitChange: (value: MetricRangeCardMetricRange) => void;
  onUpperLimitChange: (value: MetricRangeCardMetricRange) => void;
  lowerLimitErrorMessage?: string;
  upperLimitErrorMessage?: string;
  onLowerLimitBlur?: (value: number | null) => void;
  onUpperLimitBlur?: (value: number | null) => void;
  allowIncompleteLimits?: boolean;
  devMode: boolean;
};

const IdealRangeConfig = ({
  metricRangeType,
  idealRange,
  metricRangeTagOptions,
  onLowerLimitChange,
  onUpperLimitChange,
  lowerLimitErrorMessage,
  upperLimitErrorMessage,
  onLowerLimitBlur,
  onUpperLimitBlur,
  allowIncompleteLimits = false,
  devMode,
}: IdealRangeConfigProps) => {
  const { t } = useTranslation(["common"]);
  const showBoth = metricRangeType === MetricRangeType.Both;
  const showUpper = metricRangeType === MetricRangeType.Upper || showBoth;
  const showLower = metricRangeType === MetricRangeType.Lower || showBoth;
  const onChange = showLower ? onLowerLimitChange : onUpperLimitChange;
  const idealRangeDisplayName = t("common:metric_range_name_ideal");

  const { minTag, maxTag } = useMemo(() => {
    return {
      minTag: metricRangeTagOptions.find((option) => option.id === idealRange.minTagId),
      maxTag: metricRangeTagOptions.find((option) => option.id === idealRange.maxTagId),
    } as const;
  }, [idealRange.maxTagId, idealRange.minTagId, metricRangeTagOptions]);

  const rangeMinMax = `${t("common:range")}: ${buildRangeExpression(idealRange)}`;
  const tagMinMax = `${minTag?.displayName} ${lessEqualSymbol} x ${lessEqualSymbol} ${maxTag?.displayName}`;

  const previousIdealRangeMin = usePrevious(idealRange.rangeMin);
  const previousIdealRangeMax = usePrevious(idealRange.rangeMax);

  const [lowerLimit, setLowerLimit] = useState<number | null>(idealRange.rangeMin ?? null);
  const [upperLimit, setUpperLimit] = useState<number | null>(idealRange.rangeMax ?? null);

  const normalizeIdealRange = useCallback(
    (range: MetricRangeCardMetricRange): MetricRangeCardMetricRange => ({
      ...range,
      name: idealRangeDisplayName,
      color: SeverityColor.GREEN,
    }),
    [idealRangeDisplayName],
  );

  const updateParentStateIfLowerLimitValid = useCallback(
    (value: number | null, commitValue = true) => {
      const isNewLowerLimitValid = value !== null && isFinite(value);

      if (!isNewLowerLimitValid && !allowIncompleteLimits) {
        return;
      }

      if (commitValue && (isNewLowerLimitValid || allowIncompleteLimits)) {
        onLowerLimitChange(normalizeIdealRange({ ...idealRange, rangeMin: value as number }));
      }
    },
    [allowIncompleteLimits, idealRange, normalizeIdealRange, onLowerLimitChange],
  );

  const updateParentStateIfUpperLimitValid = useCallback(
    (value: number | null, commitValue = true) => {
      const isNewUpperLimitValid = value !== null && isFinite(value);

      if (!isNewUpperLimitValid && !allowIncompleteLimits) {
        return;
      }

      if (commitValue && (isNewUpperLimitValid || allowIncompleteLimits)) {
        onUpperLimitChange(normalizeIdealRange({ ...idealRange, rangeMax: value as number }));
      }
    },
    [allowIncompleteLimits, idealRange, normalizeIdealRange, onUpperLimitChange],
  );

  const handleLowerLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const proposedValue = event.target.value;
      const nextValue = proposedValue.trim();
      const isValueValid = isFinite(Number(nextValue)) || nextValue === "";

      if (!isValueValid) return;

      const nextRangeValue = nextValue === "" ? null : Number(nextValue);

      setLowerLimit(nextRangeValue);
      updateParentStateIfLowerLimitValid(nextRangeValue);
    },
    [updateParentStateIfLowerLimitValid],
  );

  const handleUpperLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const proposedValue = event.target.value;
      const nextValue = proposedValue.trim();
      const isValueValid = isFinite(Number(nextValue)) || nextValue === "";

      if (!isValueValid) return;

      const nextRangeValue = nextValue === "" ? null : Number(nextValue);

      setUpperLimit(nextRangeValue);
      updateParentStateIfUpperLimitValid(nextRangeValue);
    },
    [updateParentStateIfUpperLimitValid],
  );

  const handleLowerLimitInputBlur = useCallback(() => {
    onLowerLimitBlur?.(lowerLimit);
  }, [lowerLimit, onLowerLimitBlur]);

  const handleUpperLimitInputBlur = useCallback(() => {
    onUpperLimitBlur?.(upperLimit);
  }, [onUpperLimitBlur, upperLimit]);

  const handleEmailChange = useCallback<RangeColorNameEmailDeleteControlProps["onEmailChange"]>(
    (value) => {
      onChange(normalizeIdealRange({ ...idealRange, notificationEnabled: value }));
    },
    [idealRange, normalizeIdealRange, onChange],
  );

  //If an adjacent range is changed, this one might become valid, if so, lets update the parent state
  useEffect(() => {
    if (previousIdealRangeMin === idealRange.rangeMin) {
      updateParentStateIfLowerLimitValid(lowerLimit, lowerLimit !== (idealRange.rangeMin ?? null));
    }
  }, [idealRange.rangeMin, lowerLimit, previousIdealRangeMin, updateParentStateIfLowerLimitValid]);

  //If an adjacent range is changed, this one might become valid, if so, lets update the parent state
  useEffect(() => {
    if (previousIdealRangeMax === idealRange.rangeMax) {
      updateParentStateIfUpperLimitValid(upperLimit, upperLimit !== (idealRange.rangeMax ?? null));
    }
  }, [idealRange.rangeMax, previousIdealRangeMax, updateParentStateIfUpperLimitValid, upperLimit]);

  useEffect(() => {
    if (previousIdealRangeMin === undefined || previousIdealRangeMin === idealRange.rangeMin) return;

    const nextLowerLimit = idealRange.rangeMin ?? null;
    setLowerLimit(nextLowerLimit);
    updateParentStateIfLowerLimitValid(nextLowerLimit, false);
  }, [idealRange.rangeMin, previousIdealRangeMin, updateParentStateIfLowerLimitValid]);

  useEffect(() => {
    if (previousIdealRangeMax === undefined || previousIdealRangeMax === idealRange.rangeMax) return;

    const nextUpperLimit = idealRange.rangeMax ?? null;
    setUpperLimit(nextUpperLimit);
    updateParentStateIfUpperLimitValid(nextUpperLimit, false);
  }, [idealRange.rangeMax, previousIdealRangeMax, updateParentStateIfUpperLimitValid]);

  return (
    <Stack p="1rem" border="1px solid #5a8a5a" borderRadius="15px">
      {devMode && (
        <Stack>
          <Typography variant="caption" textAlign="center">
            {rangeMinMax}
          </Typography>
          <Typography variant="caption" textAlign="center">
            {tagMinMax}
          </Typography>
        </Stack>
      )}
      <RangeColorNameEmailDeleteControl
        enrolledInEmail={idealRange.notificationEnabled}
        name={idealRange.name}
        color={SeverityColor.GREEN}
        onDelete={noop}
        onColorChange={noop}
        onNameChange={noop}
        onEmailChange={handleEmailChange}
        hideColorPicker
        nameControl={
          <Box minHeight={56} display="flex" alignItems="center">
            <Typography>{idealRangeDisplayName}</Typography>
          </Box>
        }
      />
      <Box display="flex" gap={2} alignItems="center">
        {showLower && (
          <Box width="100%">
            <TextField
              fullWidth
              label={t("common:lower_limit")}
              type="number"
              error={Boolean(lowerLimitErrorMessage)}
              helperText={lowerLimitErrorMessage}
              value={lowerLimit ?? ""}
              onChange={handleLowerLimitChange}
              onBlur={handleLowerLimitInputBlur}
            />
          </Box>
        )}
        {showBoth && <Typography>{t("common:to")}</Typography>}
        {showUpper && (
          <Box width="100%">
            <TextField
              fullWidth
              label={t("common:upper_limit")}
              type="number"
              error={Boolean(upperLimitErrorMessage)}
              helperText={upperLimitErrorMessage}
              value={upperLimit ?? ""}
              onChange={handleUpperLimitChange}
              onBlur={handleUpperLimitInputBlur}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default IdealRangeConfig;
